import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from "react";
import { ArrowDown, ArrowRight, Check, Handshake, Video } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { submitAmbassadorApplication } from "@/lib/ambassador.functions";
import h2jLogoAsset from "@/assets/h2j-logo-cropped.png.asset.json";

export const Route = createFileRoute("/embaixadores")({
  component: AmbassadorsPage,
  head: () => ({
    meta: [
      { title: "Programa Embaixadores H2J | Descontos progressivos na mensalidade" },
      {
        name: "description",
        content:
          "Acumule descontos progressivos na mensalidade H2J compartilhando sua experiência em vídeo e indicando novas empresas. Benefícios cumulativos de até 100%.",
      },
      { property: "og:title", content: "Programa Embaixadores H2J" },
      {
        property: "og:description",
        content: "Depoimentos e indicações que viram desconto real na sua mensalidade. Cumulativos até 100%.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://www.h2jcompany.com.br/embaixadores" }],
  }),
});

const applicationSchema = z.object({
  name: z.string().trim().min(2, "Informe seu nome completo.").max(100),
  company: z.string().trim().min(2, "Informe sua empresa.").max(120),
  email: z.string().trim().email("Informe um e-mail válido.").max(255),
  whatsapp: z.string().trim().min(8, "Informe um WhatsApp válido.").max(30),
  cityState: z.string().trim().min(2, "Informe cidade e estado.").max(120),
  instagram: z.string().trim().min(2, "Informe o Instagram da empresa.").max(240),
  website: z.string().trim().max(240).optional(),
  participationMode: z.string().min(1, "Selecione como deseja participar."),
  notes: z.string().trim().max(1500).optional(),
});

const videoTiers = [
  ["5 vídeos", "10%"], ["10 vídeos", "15%"], ["15 vídeos", "20%"], ["20 vídeos", "25%"],
  ["25 vídeos", "30%"], ["30 vídeos", "35%"], ["35 vídeos", "40%"], ["40 vídeos", "45%"],
  ["45 vídeos", "50%"], ["50 vídeos", "55%"], ["55 vídeos", "60%"], ["60 vídeos", "65%"],
  ["65 vídeos", "70%"], ["70 vídeos", "75%"], ["75 vídeos", "80%"], ["80 vídeos", "85%"],
  ["85 vídeos", "90%"], ["90 vídeos", "95%"], ["95 ou mais vídeos", "100%"],
];

const contractTiers = [
  ["1 contrato", "10%"], ["2 contratos", "20%"], ["3 contratos", "30%"], ["4 contratos", "40%"],
  ["5 contratos", "50%"], ["6 contratos", "60%"], ["7 contratos", "70%"], ["8 contratos", "80%"],
  ["9 contratos", "90%"], ["10 contratos", "100%"],
];

const examples = [
  {
    label: "Exemplo 1",
    lines: ["10 depoimentos enviados = 15%", "2 indicações convertidas = 20%"],
    total: "35%",
    note: "de desconto na próxima mensalidade.",
  },
  {
    label: "Exemplo 2",
    lines: ["20 depoimentos enviados = 25%", "5 indicações convertidas = 50%"],
    total: "75%",
    note: "de desconto na próxima mensalidade.",
  },
  {
    label: "Exemplo 3",
    lines: ["30 depoimentos enviados = 35%", "7 indicações convertidas = 70%"],
    total: "100%",
    note: "Total calculado: 105%. Como o programa possui limite máximo de benefício, será aplicado 100% de desconto.",
  },
];

const rules = [
  "Participação exclusiva para clientes ativos da H2J.",
  "A participação é gratuita e voluntária.",
  "Os descontos são aplicados na mensalidade seguinte após a validação dos requisitos.",
  "Os depoimentos passam por uma análise antes da aprovação.",
  "As indicações somente serão consideradas válidas após a assinatura do contrato pelo cliente indicado e a confirmação do pagamento da primeira mensalidade. Somente após essa validação o desconto correspondente será liberado para utilização na mensalidade subsequente do Embaixador.",
  "Os benefícios das duas modalidades são cumulativos, respeitando o limite máximo de 100% de desconto sobre a mensalidade.",
  "Os descontos são pessoais, intransferíveis e não podem ser convertidos em dinheiro, crédito, reembolso ou abatimento em outras cobranças.",
];

const participationModes = [
  "Quero enviar depoimentos em vídeo.",
  "Quero indicar novas empresas.",
  "Quero participar das duas modalidades.",
];

type AnalyticsWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
};

function track(event: string, detail: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const analytics = window as AnalyticsWindow;
  analytics.dataLayer = analytics.dataLayer ?? [];
  analytics.dataLayer.push({ event, ...detail });
  analytics.gtag?.("event", event, detail);
  analytics.fbq?.("trackCustom", event, detail);
}

function AmbassadorsPage() {
  const submitApplication = useServerFn(submitAmbassadorApplication);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);
  const [sheetSynced, setSheetSynced] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  const jumpToForm = (origin: string) => {
    track("cta_click", { origin });
    document.getElementById("participar")?.scrollIntoView({ behavior: "smooth" });
  };

  const startForm = () => {
    if (formStarted) return;
    setFormStarted(true);
    track("form_start", { form_name: "embaixadores_h2j" });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const raw = {
      name: formData.get("name"),
      company: formData.get("company"),
      email: formData.get("email"),
      whatsapp: formData.get("whatsapp"),
      cityState: formData.get("cityState"),
      instagram: formData.get("instagram"),
      website: formData.get("website") || undefined,
      participationMode: formData.get("participationMode"),
      notes: formData.get("notes") || undefined,
    };
    const result = applicationSchema.safeParse(raw);
    if (!result.success) {
      setErrors(Object.fromEntries(result.error.issues.map((issue) => [String(issue.path[0]), issue.message])));
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const query = new URLSearchParams(window.location.search);
      const submission = await submitApplication({
        data: {
          ...result.data,
          participationMode: result.data.participationMode as (typeof participationModes)[number],
          utmSource: query.get("utm_source") || undefined,
          utmMedium: query.get("utm_medium") || undefined,
          utmCampaign: query.get("utm_campaign") || undefined,
        },
      });
      setSheetSynced(submission.sheetSynced);
      track("form_submit", { form_name: "embaixadores_h2j" });
      setSent(true);
      form.reset();
    } catch {
      setErrors({ form: "Não foi possível enviar agora. Tente novamente em alguns instantes." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main id="topo" className="bg-background text-foreground">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border/70 bg-background/95 backdrop-blur-md">
        <div className="section-shell grid h-18 grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
          <a href="#topo" aria-label="H2J Growth Company, início" className="min-w-0">
            <img src={h2jLogoAsset.url} alt="H2J Growth Company" width={450} height={150} className="h-8 w-auto sm:h-9" />
          </a>
          <Button variant="premium" size="sm" onClick={() => jumpToForm("header")}>
            Quero participar
          </Button>
        </div>
      </header>

      <section className="section-shell relative flex min-h-[86svh] flex-col justify-center overflow-hidden pb-16 pt-28 sm:pt-32">
        <div className="mb-9 flex items-center gap-4">
          <span className="h-px w-10 bg-foreground/40" />
          <span className="eyebrow">Programa Embaixadores H2J</span>
        </div>
        <h1 className="animate-rise max-w-4xl text-[2.6rem] leading-[1.05] sm:text-[3.4rem] lg:text-[4.6rem]">
          Crescemos <span className="text-muted-foreground">juntos.</span>
        </h1>
        <p className="mt-9 max-w-xl text-base leading-[1.75] text-muted-foreground sm:text-lg">
          Acumule descontos progressivos na sua mensalidade compartilhando sua experiência e indicando novas empresas.
          Os benefícios são cumulativos e podem chegar a 100% de desconto.
        </p>

        <div className="mt-9 flex flex-col items-start gap-3">
          <Button variant="premium" size="xl" onClick={() => jumpToForm("hero")}>
            Quero ser um Embaixador H2J <ArrowDown />
          </Button>
          <p className="text-xs text-muted-foreground">Exclusivo para clientes ativos da H2J.</p>
        </div>
        <div className="mt-14 grid max-w-3xl grid-cols-2 border-y border-foreground/25 py-4 text-[0.68rem] font-semibold uppercase tracking-[0.18em] sm:grid-cols-4">
          {["Depoimentos", "Indicações", "Cumulativo", "Até 100%"].map((item, index) => (
            <span key={item} className="py-2">
              <b className="mr-1 font-heading font-normal text-accent-ink">0{index + 1}</b> {item}
            </span>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-surface-soft py-24 sm:py-32">
        <div className="section-shell grid gap-12 lg:grid-cols-[.9fr_1.1fr]">
          <div>
            <p className="eyebrow">Confiança</p>
            <h2 className="mt-4 text-4xl font-semibold leading-tight sm:text-[2.9rem]">
              Os melhores negócios nascem da confiança.
            </h2>
          </div>
          <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
            <p>
              Criamos o Programa Embaixadores H2J, uma iniciativa exclusiva para reconhecer clientes que compartilham
              suas experiências e indicam novas empresas para fazer parte da nossa comunidade.
            </p>
            <p>
              O grande diferencial é que os benefícios das duas modalidades são cumulativos. Você pode acumular descontos
              através de depoimentos em vídeo e também através de indicações convertidas em novos clientes, somando ambos
              até atingir o limite máximo de 100% de desconto na mensalidade.
            </p>
            <p className="border-l-2 border-primary pl-6 text-xl font-bold text-foreground">
              Quanto maior sua participação, maior será sua recompensa.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="section-shell">
          <p className="eyebrow">Como funciona</p>
          <h2 className="mt-4 max-w-3xl text-4xl font-semibold sm:text-[2.9rem]">Duas formas de acumular benefícios</h2>

          <div className="mt-16 grid gap-14 lg:grid-cols-2">
            <div>
              <div className="flex items-center gap-4">
                <Video className="size-7 text-accent-ink" />
                <h3 className="text-2xl font-bold">Compartilhando sua experiência</h3>
              </div>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Grave pequenos vídeos contando como foi sua experiência com a H2J, os resultados conquistados ou sua
                percepção sobre nosso trabalho. Os depoimentos aprovados poderão ser utilizados em materiais
                institucionais, campanhas, apresentações comerciais e redes sociais.
              </p>
              <TierTable head={["Depoimentos aprovados", "Desconto"]} rows={videoTiers} />
            </div>

            <div>
              <div className="flex items-center gap-4">
                <Handshake className="size-7 text-accent-ink" />
                <h3 className="text-2xl font-bold">Indicando novas empresas</h3>
              </div>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Conhece uma empresa que pode crescer com a H2J? Indique nossos serviços e, sempre que sua indicação se
                transformar em um novo cliente, você também será recompensado.
              </p>
              <TierTable head={["Contratos fechados", "Desconto"]} rows={contractTiers} />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface-dark py-24 text-background sm:py-32">
        <div className="section-shell">
          <p className="eyebrow">Benefícios cumulativos</p>
          <h2 className="mt-4 max-w-3xl text-4xl font-semibold sm:text-[2.9rem]">
            Os descontos das duas modalidades podem ser somados
          </h2>
          <div className="mt-14 grid gap-px bg-background/15 lg:grid-cols-3">
            {examples.map((example) => (
              <div key={example.label} className="bg-surface-dark p-8">
                <p className="eyebrow">{example.label}</p>
                <div className="mt-6 space-y-3 text-sm text-background/85">
                  {example.lines.map((line) => (
                    <p key={line} className="border-b border-background/15 pb-3">
                      {line}
                    </p>
                  ))}
                </div>
                <p className="mt-7 font-heading text-6xl font-semibold text-accent-ink">{example.total}</p>
                <p className="mt-3 text-sm leading-relaxed text-background/80">{example.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-surface-soft py-24 sm:py-32">
        <div className="section-shell grid gap-12 lg:grid-cols-[.7fr_1.3fr]">
          <div>
            <p className="eyebrow">Transparência</p>
            <h2 className="mt-4 text-4xl font-semibold sm:text-[2.9rem]">Regras do Programa</h2>
          </div>
          <ol className="border-t border-foreground">
            {rules.map((rule, index) => (
              <li key={rule} className="grid grid-cols-[auto_1fr] gap-5 border-b border-border py-5">
                <span className="text-xs font-bold text-accent-ink">{String(index + 1).padStart(2, "0")}</span>
                <p className="leading-relaxed text-muted-foreground">{rule}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="participar" className="py-24 sm:py-32">
        <div className="section-shell grid gap-12 lg:grid-cols-[.7fr_1.3fr]">
          <div>
            <p className="eyebrow">Inscrição</p>
            <h2 className="mt-4 text-4xl font-semibold sm:text-[2.9rem]">Quero participar</h2>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              Preencha o formulário abaixo para solicitar sua participação. Nossa equipe analisará seu cadastro e enviará
              todas as orientações para ativação do seu acesso ao programa.
            </p>
          </div>
          {sent ? (
            <div className="self-start border-t-2 border-primary bg-surface-soft p-8 sm:p-10">
              <Check className="size-10 text-accent-ink" />
              <h3 className="mt-6 text-3xl font-bold">Recebemos suas informações.</h3>
              <p className="mt-4 max-w-xl leading-relaxed text-muted-foreground">
                A equipe H2J vai analisar seu cadastro e entrar em contato com as orientações para ativação do seu acesso
                ao Programa Embaixadores.
              </p>
              {!sheetSynced && <p className="mt-4 text-sm text-destructive">Recebemos suas informações, mas não conseguimos enviá-las à planilha agora. Nossa equipe já tem seu cadastro.</p>}
            </div>
          ) : (
            <form onSubmit={submit} onFocus={startForm} noValidate className="grid gap-5 sm:grid-cols-2">
              <FormField name="name" label="Nome completo" autoComplete="name" error={errors["name"]} />
              <FormField name="company" label="Empresa" autoComplete="organization" error={errors["company"]} />
              <FormField name="email" label="E-mail" inputMode="email" autoComplete="email" error={errors["email"]} />
              <FormField name="whatsapp" label="WhatsApp" inputMode="tel" autoComplete="tel" error={errors["whatsapp"]} />
              <FormField name="cityState" label="Cidade / Estado" error={errors["cityState"]} />
              <FormField name="instagram" label="Instagram da empresa" error={errors["instagram"]} />
              <FormField name="website" label="Site (opcional)" error={errors["website"]} />
              <SelectField
                name="participationMode"
                label="Como deseja participar?"
                options={participationModes}
                error={errors["participationMode"]}
              />
              <label className="sm:col-span-2">
                <span className="mb-2 block text-xs font-bold uppercase">Observações</span>
                <textarea
                  name="notes"
                  rows={4}
                  maxLength={1500}
                  className="w-full rounded-sm border border-input bg-background p-4 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
                />
                {errors["notes"] && <ErrorText>{errors["notes"]}</ErrorText>}
              </label>
              {errors["form"] && <p className="text-sm font-semibold text-destructive sm:col-span-2">{errors["form"]}</p>}
              <div className="sm:col-span-2">
                <Button variant="premium" size="xl" type="submit" disabled={submitting}>
                  {submitting ? "Enviando..." : "Quero ser um Embaixador H2J"} <ArrowRight />
                </Button>
                <p className="mt-4 max-w-xl text-xs leading-relaxed text-muted-foreground">
                  Participação gratuita e voluntária, exclusiva para clientes ativos da H2J.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      <section className="bg-surface-dark py-24 text-background sm:py-32">
        <div className="section-shell">
          <h2 className="max-w-4xl text-4xl font-semibold leading-tight sm:text-[2.9rem]">
            Seu crescimento pode gerar o crescimento de outras empresas.
          </h2>
          <p className="mt-7 max-w-2xl text-xl text-background/80">
            E ser recompensado por isso, mês após mês, na sua própria mensalidade.
          </p>
          <Button className="mt-10" variant="premium" size="xl" onClick={() => jumpToForm("final_cta")}>
            Quero participar do programa <ArrowRight />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-12">
        <div className="section-shell grid gap-8 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <img src={h2jLogoAsset.url} alt="H2J Growth Company" width={450} height={150} loading="lazy" decoding="async" className="h-8 w-auto" />
            <p className="mt-3 text-sm text-muted-foreground">H2J, Estratégia. Aquisição. Crescimento.</p>
            <div className="mt-4 flex gap-6 text-xs text-muted-foreground">
              <a href="/privacidade" className="hover:text-accent-ink">Política de Privacidade</a>
              <a href="/termos" className="hover:text-accent-ink">Termos de Uso</a>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 text-sm font-semibold">
            <a href="/" className="hover:text-accent-ink">Site H2J</a>
            <a href="https://instagram.com/h2jcompany" target="_blank" rel="noreferrer" className="hover:text-accent-ink">
              Instagram
            </a>
            <a href="mailto:contato@h2jcompany.com.br" className="hover:text-accent-ink">E-mail</a>
            <a href="https://wa.me/5511971488408?text=vim%20através%20do%20seu%20site%20e%20gostaria%20de%20agendar%20uma%20reunião%20com%20especialista." target="_blank" rel="noreferrer" className="hover:text-accent-ink">WhatsApp</a>
          </div>
        </div>
        <div className="section-shell mt-8 text-xs text-muted-foreground">
          © {new Date().getFullYear()} H2J Growth Company. Todos os direitos reservados.
        </div>
      </footer>
    </main>
  );
}

function TierTable({ head, rows }: { head: [string, string]; rows: string[][] }) {
  return (
    <div className="mt-8 border-t-2 border-foreground">
      <div className="grid grid-cols-[1fr_auto] gap-4 border-b border-foreground py-3 text-[0.68rem] font-semibold uppercase tracking-[0.18em]">
        <span>{head[0]}</span>
        <span className="text-accent-ink">{head[1]}</span>
      </div>
      {rows.map(([label, discount]) => (
        <div key={label} className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-border py-3 text-sm">
          <span className="font-semibold">{label}</span>
          <span className="font-heading font-semibold text-accent-ink">{discount}</span>
        </div>
      ))}
    </div>
  );
}

function FormField({
  name,
  label,
  error,
  ...props
}: { name: string; label: string; error: string | undefined } & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "autoComplete" | "inputMode"
>) {
  return (
    <label>
      <span className="mb-2 block text-xs font-bold uppercase">{label}</span>
      <input
        name={name}
        maxLength={255}
        {...props}
        className="h-12 w-full rounded-sm border border-input bg-background px-4 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
      />
      {error && <ErrorText>{error}</ErrorText>}
    </label>
  );
}

function SelectField({
  name,
  label,
  options,
  error,
}: {
  name: string;
  label: string;
  options: string[];
  error: string | undefined;
}) {
  return (
    <label>
      <span className="mb-2 block text-xs font-bold uppercase">{label}</span>
      <select
        name={name}
        defaultValue=""
        className="h-12 w-full rounded-sm border border-input bg-background px-4 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
      >
        <option value="" disabled>
          Selecione
        </option>
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
      {error && <ErrorText>{error}</ErrorText>}
    </label>
  );
}

function ErrorText({ children }: { children: ReactNode }) {
  return <span className="mt-1 block text-xs text-destructive">{children}</span>;
}
